import {canUserMarkUnreadOrRead, ProviderEvent} from "./domain";
import {ProviderEventListAction} from "./providerEventListReducer";
import {Dispatch, useCallback, useEffect} from "react";
import {useMarkProviderEventsAsRead} from "./useMarkProviderEventsAsRead";
import {useObservable} from "../../rxsupport";
import {ProviderEventUnreadAction} from "./ProviderEventUnreadCount";
import {useMarkProviderEventsAsUnread} from "./useMarkProviderEventsAsUnRead";
import {CurrentUserData} from "../../security";


type MarkAsReadButtonProps = {
    refetchFunc: (() => void) | null;
    selected: ProviderEvent;
    cantMarkUnreadOrRead: boolean;
    dispatch: Dispatch<ProviderEventListAction>;
    dispatchProviderEventUnread: Dispatch<ProviderEventUnreadAction>;
}
const MarkAsReadButton = ({
                              refetchFunc,
                              selected,
                              cantMarkUnreadOrRead,
                              dispatchProviderEventUnread,
                              dispatch,
                          }: MarkAsReadButtonProps) => {
    const {
        isRunning$,
        execute
    } = useMarkProviderEventsAsRead(() => {
        if (refetchFunc) {
            refetchFunc();
        }

        dispatch({type: "SET_SELECTED", selected: null});
        dispatchProviderEventUnread({type: 'DECREASE', num: 1});
    });

    const isRunning = useObservable(isRunning$, false)
    const isDisabled = isRunning || cantMarkUnreadOrRead;
    return (
        <button
            className={`bg-atlantis-200 hover:bg-atlantis-300 text-white py-2 px-4 ${isDisabled ? "opacity-50 " : ""} 
                                        w-140px rounded`}
            disabled={isDisabled}
            onClick={() => execute([selected.guid])}
        >
            Mark as read
        </button>
    )
}

type MarkAsUnreadButtonProps = {
    refetchFunc: (() => void) | null;
    selected: ProviderEvent;
    dispatch: Dispatch<ProviderEventListAction>;
    dispatchProviderEventUnread: Dispatch<ProviderEventUnreadAction>;
    cantMarkUnreadOrRead: boolean;
}
const MarkAsUnreadButton = ({
                                refetchFunc,
                                selected,
                                dispatchProviderEventUnread,
                                dispatch,
                                cantMarkUnreadOrRead
                            }: MarkAsUnreadButtonProps) => {
    const {
        isRunning$,
        execute
    } = useMarkProviderEventsAsUnread(() => {
        if (refetchFunc) {
            refetchFunc();
        }

        dispatch({type: "SET_SELECTED", selected: null});
        dispatchProviderEventUnread({type: 'INCREASE', num: 1});
    });

    const isRunning = useObservable(isRunning$, false)
    const isDisabled = isRunning || cantMarkUnreadOrRead;

    return (
        <button
            className={`bg-atlantis-200 hover:bg-atlantis-300 text-white py-2 px-4 ${isDisabled ? "opacity-50 cursor-not-allowed" : ""} 
                                        w-140px rounded`}
            disabled={isDisabled}
            onClick={() => execute([selected.guid])}
        >
            Mark as unread
        </button>
    )
}

type ProviderEventListMarkReadModalProps = {
    selected: ProviderEvent | null;
    currentUser: CurrentUserData | null;
    refetchFunc: (() => void) | null,
    dispatch: Dispatch<ProviderEventListAction>
    dispatchProviderEventUnread: Dispatch<ProviderEventUnreadAction>
}

export const ProviderEventListMarkReadModal = ({
                                                   currentUser,
                                                   selected,
                                                   dispatch,
                                                   refetchFunc,
                                                   dispatchProviderEventUnread
                                               }: ProviderEventListMarkReadModalProps) => {


    const escFunction = useCallback((event) => {
        if (event.keyCode === 27) {
            dispatch({type: "SET_SELECTED", selected: null});
        }
    }, [dispatch]);

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    if (selected === null) {
        return <div/>;
    }

    const cantMarkReadOrUnread = !canUserMarkUnreadOrRead(currentUser, selected);

    return (
        <div className="bg-atlantis-400 rounded-l provider-mark-read-modal-position">
            <div className='px-10 py-5 flex justify-between relative'>
                {
                    selected && <>
                        <button
                            onClick={() => dispatch({type: "SET_SELECTED", selected: null})}
                            className='bg-black border border-white text-white py-2 px-4 rounded w-120px'>
                            Cancel
                        </button>
                        {!selected?.viewedAt &&
                            <MarkAsReadButton dispatch={dispatch}
                                              cantMarkUnreadOrRead={cantMarkReadOrUnread}
                                              refetchFunc={refetchFunc}
                                              selected={selected}
                                              dispatchProviderEventUnread={dispatchProviderEventUnread}/>
                        }
                        {selected?.viewedAt &&
                            <MarkAsUnreadButton dispatch={dispatch}
                                                cantMarkUnreadOrRead={cantMarkReadOrUnread}
                                                refetchFunc={refetchFunc}
                                                selected={selected}
                                                dispatchProviderEventUnread={dispatchProviderEventUnread}/>
                        }
                    </>
                }
                {
                    cantMarkReadOrUnread &&
                    <div className="absolute text-xs text-red-500 bottom-0 right-0 pr-2">Only report submitter can mark {selected.viewedAt ? "unread" : "read"}</div>
                }
            </div>
        </div>
    );
}
